/* Luxcut storefront behaviour, loaded once (defer) by layout/luxcut.liquid.
   Everything here is progressive enhancement: links, forms and the native
   variant <select> keep working without it. Each feature initialises only
   when its markup is on the page. */
(() => {
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];
  const store = (kind) => { try { return window[kind]; } catch (e) { return null; } };
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Scroll a gallery track to one slide by exact offset (scrollIntoView fights scroll-snap
  // and can stop part-way between slides).
  const showSlide = (track, slide) => {
    const first = track.firstElementChild;
    if (!(slide instanceof HTMLElement) || !(first instanceof HTMLElement)) return;
    track.scrollTo({ left: slide.offsetLeft - first.offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
  };

  /* ---------- looping card strips (homepage collections, customer showcase) ----------
     The real cards are copied until one "set" is wider than any screen, and three sets
     sit side by side. Auto-advance slides the track with a transform, which moves in
     fractions of a pixel (scrollLeft only takes whole pixels, so moving it by less than
     one per frame stutters). The moment someone touches, swipes, drags or scrolls the
     strip, that offset is folded into scrollLeft and native scrolling takes over. The
     loop re-centres on the middle set only once a swipe has settled: changing scrollLeft
     during momentum scrolling stops it dead on phones. Returns { mark } so a caller can
     restart the 3s pause, and { cards } (real cards followed by their inert copies). */
  window.LuxLoopStrip = (viewport, track, { speed = 0.05, isPaused = () => false } = {}) => {
    const real = [...track.children];
    if (!real.length) return null;
    const realW = track.scrollWidth;
    const sets = Math.max(1, Math.ceil(Math.max(window.screen?.width || 0, window.innerWidth) / Math.max(realW, 1)));
    for (let k = 1; k < sets * 3; k++) {
      for (const c of real) {
        const copy = /** @type {HTMLElement} */ (c.cloneNode(true));
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('data-shopify-editor-block');
        if (copy.matches('a, button')) copy.tabIndex = -1;
        copy.querySelectorAll('a, button').forEach((el) => { /** @type {HTMLElement} */ (el).tabIndex = -1; });
        track.appendChild(copy);
      }
    }
    const cards = /** @type {HTMLElement[]} */ ([...track.children]);
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;

    let setW = 0, offset = 0, lastInteraction = 0, ignoreUntil = 0, settle = 0;
    let lastT = null, onScreen = false, running = false;
    let pressed = false, dragging = false, justDragged = false, dragX = 0, dragLeft = 0;
    const write = (left) => { ignoreUntil = performance.now() + 80; viewport.scrollLeft = left; };
    const paint = () => { track.style.transform = offset ? `translate3d(${-offset}px,0,0)` : ''; };
    const wrap = (pos) => {
      if (!setW) return pos;
      while (pos < setW * 0.5) pos += setW;
      while (pos >= setW * 1.5) pos -= setW;
      return pos;
    };
    const mark = () => { lastInteraction = performance.now(); };
    // hand the auto-advance offset over to the real scroll position
    const commit = () => {
      if (!offset) return;
      const pos = wrap(viewport.scrollLeft + offset);
      offset = 0;
      paint();
      write(Math.round(pos));
    };
    const takeOver = () => { mark(); commit(); };
    const measure = () => {
      const pos = viewport.scrollLeft + offset;
      const frac = setW ? pos / setW : 1;
      offset = 0;
      paint();
      const first = cards[0], next = cards[real.length * sets];
      setW = first && next ? next.offsetLeft - first.offsetLeft : track.scrollWidth / 3;
      write(Math.round(wrap(frac * setW)));
    };
    const recentre = () => {
      const pos = viewport.scrollLeft, fixed = wrap(pos);
      if (fixed === pos) return;
      if (dragging) dragLeft += fixed - pos;
      write(fixed);
    };

    requestAnimationFrame(() => requestAnimationFrame(measure));
    let measuredWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (window.innerWidth === measuredWidth) return;
      measuredWidth = window.innerWidth;
      measure();
    });

    viewport.addEventListener('scroll', () => {
      if (performance.now() < ignoreUntil) return;
      mark();
      clearTimeout(settle);
      const max = viewport.scrollWidth - viewport.clientWidth;
      // at either end the swipe has already stopped, so re-centre straight away
      if (viewport.scrollLeft <= 1 || viewport.scrollLeft >= max - 1) recentre();
      else settle = window.setTimeout(recentre, 150);
    }, { passive: true });
    for (const type of ['wheel', 'touchstart', 'focusin']) viewport.addEventListener(type, takeOver, { passive: true });

    // Mouse drag-to-scroll (touch scrolls natively). A drag starts only after a few px,
    // so a plain click still works; the click that ends a real drag is swallowed.
    viewport.addEventListener('pointerdown', (e) => {
      takeOver();
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      pressed = true; dragX = e.clientX; dragLeft = viewport.scrollLeft;
    });
    viewport.addEventListener('pointermove', (e) => {
      if (!pressed) return;
      if (!dragging && Math.abs(e.clientX - dragX) > 5) {
        dragging = true; viewport.classList.add('dragging'); viewport.setPointerCapture(e.pointerId);
      }
      if (!dragging) return;
      write(dragLeft - (e.clientX - dragX));
      mark();
    });
    const endDrag = () => {
      justDragged = dragging; pressed = false; dragging = false;
      viewport.classList.remove('dragging'); mark(); recentre();
    };
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('click', (e) => {
      if (!justDragged) return;
      e.preventDefault(); e.stopPropagation(); justDragged = false;
    }, true);
    viewport.addEventListener('dragstart', (e) => e.preventDefault());

    const tick = (t) => {
      if (lastT == null) lastT = t;
      const dt = Math.min(t - lastT, 50);
      lastT = t;
      if (!still && setW && !pressed && !isPaused() && performance.now() - lastInteraction > 3000) {
        offset += dt * speed;
        // the sets are identical, so stepping back a whole set is invisible
        if (viewport.scrollLeft + offset >= setW * 1.5) offset -= setW;
        paint();
      }
      if (onScreen) requestAnimationFrame(tick); else { running = false; lastT = null; }
    };
    new IntersectionObserver(([e]) => {
      onScreen = e.isIntersecting;
      if (onScreen && !running) { running = true; requestAnimationFrame(tick); }
    }).observe(viewport);

    return { mark, cards };
  };

  /* ---------- promo bar ---------- */
  const promo = qs('[data-lux-promo]');
  if (promo) {
    qs('[data-lux-promo-close]', promo)?.addEventListener('click', () => {
      document.documentElement.classList.add('promo-dismissed');
      try { store('localStorage')?.setItem('promoDismissed', '1'); } catch (e) {}
      // the landing stage is positioned under the bar and re-measures on this
      document.dispatchEvent(new CustomEvent('lux:promo-closed'));
      qs('[data-lux-menu-toggle]')?.focus();
    });
  }

  /* ---------- clear header on inner pages ---------- */
  // Like the homepage: solid at the very top, clear once scrolled. Over a dark band the
  // icons turn light, judged by the first opaque background behind the cart/menu buttons.
  const innerHeader = document.body.classList.contains('lux--inner') ? qs('[data-lux-header]') : null;
  if (innerHeader) {
    const actions = qs('.lux-header__actions', innerHeader);
    const top = innerHeader.closest('.lux-header-section') || innerHeader.closest('.lux-top') || innerHeader;
    const darkBehind = () => {
      if (!actions) return false;
      const r = actions.getBoundingClientRect();
      for (const hit of document.elementsFromPoint(r.left + r.width / 2, r.top + r.height / 2)) {
        if (top.contains(hit)) continue;
        for (let n = hit; n && n !== document.documentElement; n = n.parentElement) {
          if (/^(IMG|VIDEO|CANVAS|PICTURE)$/.test(n.tagName)) return false;
          const c = getComputedStyle(n);
          if (c.backgroundImage !== 'none') return false;
          const m = c.backgroundColor.match(/[\d.]+/g);
          if (m && (m[3] === undefined || +m[3] > 0.5)) return (0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2]) / 255 < 0.5;
        }
      }
      return false;
    };
    let pending = false;
    const update = () => {
      pending = false;
      const clear = window.scrollY > 2;
      innerHeader.classList.toggle('is-clear', clear);
      innerHeader.classList.toggle('on-dark', clear && darkBehind());
    };
    window.addEventListener('scroll', () => { if (!pending) { pending = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  /* ---------- header menu (disclosure) ---------- */
  const toggle = qs('[data-lux-menu-toggle]');
  const menu = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
  if (toggle && menu) {
    const setOpen = (open, { restoreFocus = false } = {}) => {
      toggle.setAttribute('aria-expanded', String(open));
      menu.classList.toggle('is-open', open);
      if (open) qs('a', menu)?.focus();
      else if (restoreFocus) toggle.focus();
    };
    toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) setOpen(false, { restoreFocus: true });
    });
    document.addEventListener('click', (e) => {
      if (!menu.classList.contains('is-open')) return;
      if (e.target instanceof Node && !menu.contains(e.target) && !toggle.contains(e.target)) setOpen(false);
    });
    menu.addEventListener('focusout', (e) => {
      if (e.relatedTarget instanceof Node && !menu.contains(e.relatedTarget) && e.relatedTarget !== toggle) setOpen(false);
    });
    for (const a of qsa('a', menu)) {
      // in-page anchors (e.g. #shop on the homepage) don't navigate away, so close explicitly
      a.addEventListener('click', () => setOpen(false));
    }

    // At the top of the page the links sit in the bar, if they fit; once the visitor
    // scrolls past the bar, the burger and its panel take over.
    const header = toggle.closest('.lux-header');
    const bar = header?.closest('.lux-top') || header;
    const groups = qsa('.lux-menu__group', menu);
    const list = qs('ul', menu);
    if (header && bar && list) {
      let fits = false;
      let inline = null;
      // Switch modes without the panel's fade: going from links-in-the-bar (fully visible)
      // to the hidden panel would otherwise fade it out in place, which reads as a flash.
      const instantly = (fn) => {
        menu.style.transition = 'none';
        fn();
        void menu.offsetWidth;
        menu.style.transition = '';
      };
      const sync = () => {
        const want = fits && window.scrollY <= bar.offsetHeight;
        if (want === inline) return;
        inline = want;
        if (want) {
          if (menu.classList.contains('is-open')) setOpen(false);
          // sub-links start closed in the bar (the panel may have them open on their own pages)
          for (const g of groups) if (g instanceof HTMLDetailsElement) g.open = false;
        }
        instantly(() => header.classList.toggle('is-inline', want));
      };
      // lay the links out in the bar for a moment and check they clear the logo and the cart
      const logo = qs('.lux-header__logo', header);
      const actions = qs('.lux-header__actions', header);
      const measure = () => {
        instantly(() => {
          const was = header.classList.contains('is-inline');
          header.classList.add('is-inline');
          const links = list.getBoundingClientRect();
          fits = header.scrollWidth <= header.clientWidth + 1
            && (!logo || links.left >= logo.getBoundingClientRect().right + 16)
            && (!actions || links.right <= actions.getBoundingClientRect().left - 16);
          header.classList.toggle('is-inline', was);
        });
        inline = null;
        sync();
      };
      let queued = false;
      window.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; sync(); });
      }, { passive: true });
      // phones fire resize as the address bar slides away while scrolling; only a width change matters
      let measuredWidth = window.innerWidth;
      window.addEventListener('resize', () => {
        if (window.innerWidth === measuredWidth) return;
        measuredWidth = window.innerWidth;
        measure();
      });
      document.addEventListener('lux:promo-closed', measure);
      document.fonts?.ready.then(measure);
      measure();

      // in the bar, Air Fresheners' sub-links close on an outside click or Escape
      document.addEventListener('click', (e) => {
        if (!header.classList.contains('is-inline')) return;
        for (const g of groups) if (g instanceof HTMLDetailsElement && g.open && e.target instanceof Node && !g.contains(e.target)) g.open = false;
      });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !header.classList.contains('is-inline')) return;
        for (const g of groups) {
          if (g instanceof HTMLDetailsElement && g.open) { g.open = false; qs('summary', g)?.focus(); }
        }
      });
    }
  }

  /* ---------- quantity steppers ---------- */
  for (const wrap of qsa('[data-lux-qty]')) {
    const input = qs('input', wrap);
    if (!(input instanceof HTMLInputElement)) continue;
    for (const btn of qsa('[data-lux-qty-step]', wrap)) {
      btn.addEventListener('click', () => {
        const step = Number(btn.getAttribute('data-lux-qty-step'));
        const min = input.min === '' ? 0 : Number(input.min);
        const next = Math.max(min, (Number(input.value) || 0) + step);
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  /* ---------- auto-submit selects (collection sort) ---------- */
  for (const select of qsa('[data-lux-autosubmit]')) {
    select.addEventListener('change', () => select.form?.requestSubmit());
  }

  /* ---------- product option picker ---------- */
  for (const root of qsa('[data-lux-product]')) {
    const dataEl = qs('[data-lux-variants]', root);
    const select = qs('[data-lux-variant-select]', root);
    if (!dataEl || !(select instanceof HTMLSelectElement)) continue;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { continue; }
    const fieldsets = qsa('[data-lux-option]', root);
    // price and add-to-cart appear more than once (header, button, phone bar)
    const priceEls = qsa('[data-lux-price]', root);
    const compareEl = qs('[data-lux-compare]', root);
    const addBtns = qsa('[data-lux-add]', root);
    const status = qs('[data-lux-variant-status]', root);
    const gallery = qs('[data-lux-gallery]', root);
    const setButtons = (disabled, key) => {
      for (const btn of addBtns) {
        btn.disabled = disabled;
        const label = qs('[data-lux-add-label]', btn);
        if (label && btn.dataset[key]) label.textContent = btn.dataset[key];
      }
    };

    const current = () => fieldsets.map((fs) => {
      const checked = qs('input:checked', fs);
      return checked instanceof HTMLInputElement ? checked.value : null;
    });

    const apply = () => {
      const chosen = current();
      const variant = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      fieldsets.forEach((fs, i) => {
        const label = qs('[data-lux-option-value]', fs);
        if (label) label.textContent = chosen[i] ?? '';
      });
      const first = addBtns[0];
      if (!variant) {
        setButtons(true, 'unavailable');
        if (status) status.textContent = first?.dataset.unavailable ?? '';
        return;
      }
      select.value = String(variant.id);
      for (const el of priceEls) el.textContent = variant.price;
      if (compareEl) {
        compareEl.textContent = variant.compare ?? '';
        compareEl.hidden = !variant.compare;
      }
      setButtons(!variant.available, variant.available ? 'add' : 'soldout');
      if (status) status.textContent = `${variant.title} — ${variant.price}${variant.available ? '' : ` — ${first?.dataset.soldout ?? ''}`}`;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState(history.state, '', url);
      if (variant.media && gallery) {
        const track = qs('[data-lux-gallery-track]', gallery);
        const slide = qs(`[data-media-id="${variant.media}"]`, gallery);
        if (track && slide) showSlide(track, slide);
      }
    };
    for (const fs of fieldsets) fs.addEventListener('change', apply);
  }

  /* ---------- phone add-to-cart bar: shown once the main button has scrolled away ---------- */
  for (const bar of qsa('[data-lux-sticky-buy]')) {
    const main = qs('[data-lux-add-main]', bar.closest('[data-lux-product]') || document);
    if (!main) continue;
    bar.hidden = false;
    new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', past);
      bar.inert = !past;
    }).observe(main);
    bar.inert = true;
  }

  /* ---------- product gallery thumbnails ---------- */
  for (const gallery of qsa('[data-lux-gallery]')) {
    const track = qs('[data-lux-gallery-track]', gallery);
    const thumbs = qsa('[data-lux-thumb]', gallery);
    if (!track || !thumbs.length) continue;
    const slides = qsa('[data-media-id]', track);
    const mark = (i) => thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        showSlide(track, slides[i]);
        mark(i);
      });
    });
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) if (entry.isIntersecting) mark(slides.indexOf(entry.target));
    }, { root: track, threshold: 0.6 });
    slides.forEach((s) => io.observe(s));
  }

  /* ---------- custom-order photo upload: preview, drag-over state, size/type check ---------- */
  for (const zone of qsa('[data-lux-upload]')) {
    const input = qs('input[type="file"]', zone);
    if (!(input instanceof HTMLInputElement)) continue;
    const empty = qs('.lux-upload__empty', zone);
    const filled = qs('.lux-upload__filled', zone);
    const preview = qs('.lux-upload__preview', zone);
    const name = qs('[data-lux-upload-name]', zone);
    const error = document.getElementById((input.getAttribute('aria-describedby') || '').split(' ').pop() || '');
    const maxBytes = (Number(input.dataset.maxMb) || 20) * 1024 * 1024;
    let objectUrl = null;
    const showError = (msg) => {
      input.setCustomValidity(msg);
      if (error) { error.textContent = msg; error.hidden = !msg; }
    };
    const reset = () => {
      if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null; }
      zone.classList.remove('is-filled');
      if (empty) empty.hidden = false;
      if (filled) filled.hidden = true;
    };
    input.addEventListener('change', () => {
      reset();
      const file = input.files && input.files[0];
      if (!file) { showError(''); return; }
      const isImage = /^image\//.test(file.type) || /\.(heic|heif)$/i.test(file.name);
      if (!isImage) { input.value = ''; showError(zone.dataset.msgType || ''); return; }
      if (file.size > maxBytes) { input.value = ''; showError(zone.dataset.msgLarge || ''); return; }
      showError('');
      zone.classList.add('is-filled');
      if (empty) empty.hidden = true;
      if (filled) filled.hidden = false;
      if (name) name.textContent = file.name;
      if (preview instanceof HTMLImageElement) {
        // HEIC can't be previewed in most browsers; the file name still shows
        objectUrl = URL.createObjectURL(file);
        preview.hidden = false;
        preview.onerror = () => { preview.hidden = true; };
        preview.src = objectUrl;
      }
    });
    for (const type of ['dragenter', 'dragover']) zone.addEventListener(type, () => zone.classList.add('is-dragover'));
    for (const type of ['dragleave', 'drop']) zone.addEventListener(type, () => zone.classList.remove('is-dragover'));
  }

  /* ---------- quote inquiry (contact form) with photo uploads ----------
     Contact forms can't carry files, so photos ride a temporary cart line on
     the inquiry product: Shopify stores line-item files on its CDN and returns
     their links. The line is removed straight away and the links go into the
     inquiry before the form is sent normally. */
  for (const configEl of qsa('[data-lux-inquiry-config]')) {
    const form = document.getElementById(configEl.getAttribute('data-lux-inquiry-config') || '');
    if (!(form instanceof HTMLFormElement)) continue;
    // menu links like ?type=Acrylic+sign pre-select the project type
    const wantedType = new URLSearchParams(location.search).get('type')?.trim().toLowerCase();
    const typeSelect = qs('[data-lux-inquiry-type]', form);
    if (wantedType && typeSelect instanceof HTMLSelectElement) {
      const match = [...typeSelect.options].find((o) => o.value.trim().toLowerCase() === wantedType);
      if (match) typeSelect.value = match.value;
    }
    let config;
    try { config = JSON.parse(configEl.textContent); } catch (e) { continue; }
    const wrap = qs('[data-lux-inquiry-photos]', form);
    const input = qs('[data-lux-inquiry-input]', form);
    const list = qs('[data-lux-inquiry-list]', form);
    const links = qs('[data-lux-inquiry-links]', form);
    const error = qs('[data-lux-inquiry-error]', form);
    const status = qs('[data-lux-inquiry-status]', form);
    const submit = qs('[data-lux-inquiry-submit]', form);
    if (!wrap || !(input instanceof HTMLInputElement) || !list || !(links instanceof HTMLTextAreaElement) || !(submit instanceof HTMLButtonElement)) continue;

    wrap.hidden = false;
    const fallback = qs('[data-lux-inquiry-fallback]', form);
    if (fallback) fallback.hidden = true;

    const maxFiles = Number(config.maxFiles) || 5;
    const maxBytes = (Number(config.maxMb) || 15) * 1024 * 1024;
    /** @type {{file: File, url: string}[]} */
    let files = [];
    let busy = false;
    const sendLabel = submit.textContent;
    const showError = (msg) => { if (error) { error.textContent = msg; error.hidden = !msg; } };
    const announce = (msg) => { if (status) status.textContent = msg; };

    const render = () => {
      list.replaceChildren(...files.map((entry, i) => {
        const li = document.createElement('li');
        li.className = 'lux-inquiry__file-row';
        const img = document.createElement('img');
        img.className = 'lux-inquiry__thumb'; img.alt = ''; img.width = 52; img.height = 52;
        img.onerror = () => { img.style.visibility = 'hidden'; };   // HEIC can't be previewed in most browsers
        img.src = entry.url;
        const name = document.createElement('span');
        name.className = 'lux-inquiry__file-name'; name.textContent = entry.file.name;
        const remove = document.createElement('button');
        remove.type = 'button'; remove.className = 'lux-inquiry__remove';
        remove.textContent = config.remove;
        remove.setAttribute('aria-label', `${config.remove}: ${entry.file.name}`);
        remove.addEventListener('click', () => {
          URL.revokeObjectURL(entry.url);
          files.splice(i, 1);
          render();
          showError('');
          input.focus();
        });
        li.append(img, name, remove);
        return li;
      }));
    };

    input.addEventListener('change', () => {
      showError('');
      const picked = [...(input.files || [])];
      input.value = '';   // lets the same file be picked again after removing it
      let problem = '';
      for (const file of picked) {
        const isImage = /^image\//.test(file.type) || /\.(heic|heif)$/i.test(file.name);
        if (!isImage) { problem = config.msgType; continue; }
        if (file.size > maxBytes) { problem = config.msgTooLarge; continue; }
        if (files.length >= maxFiles) { problem = config.msgTooMany; break; }
        files.push({ file, url: URL.createObjectURL(file) });
      }
      render();
      showError(problem);
      if (picked.length) announce(`${config.added} ${files.length}/${maxFiles}`);
    });
    const drop = input.closest('label');
    if (drop) {
      for (const type of ['dragenter', 'dragover']) drop.addEventListener(type, () => drop.classList.add('is-dragover'));
      for (const type of ['dragleave', 'drop']) drop.addEventListener(type, () => drop.classList.remove('is-dragover'));
    }

    const root = window.Shopify?.routes?.root || '/';
    const upload = async () => {
      const body = new FormData();
      body.append('id', String(config.variant));
      body.append('quantity', '1');
      files.forEach((entry, i) => body.append(`properties[${config.property} ${i + 1}]`, entry.file));
      const res = await fetch(`${root}cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`upload ${res.status}`);
      const item = await res.json();
      const urls = Object.entries(item.properties || {})
        .filter(([key, value]) => key.startsWith(config.property) && typeof value === 'string' && value)
        .map(([, value]) => value);
      // the cart line was only a carrier for the files; take it straight back out
      try {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      } catch (e) { /* the inquiry still goes through; the line can be removed from the cart */ }
      if (urls.length !== files.length) throw new Error('missing links');
      return urls;
    };

    form.addEventListener('submit', async (e) => {
      if (!files.length) { links.value = ''; return; }   // nothing to upload: send as a normal form
      e.preventDefault();
      if (busy) return;
      busy = true;
      showError('');
      submit.disabled = true;
      submit.textContent = submit.dataset.uploading || sendLabel;
      announce(submit.textContent);
      try {
        const urls = await upload();
        links.value = urls.join('\n');
        form.submit();   // native submit: the browser already validated the fields
      } catch (err) {
        busy = false;
        submit.disabled = false;
        submit.textContent = sendLabel;
        showError(config.msgFailed);
        announce('');
      }
    });
  }
  // after an inquiry is sent, move focus to the confirmation
  qs('[data-lux-inquiry-done]')?.focus();

  /* ---------- newsletter popup: once per session, after the car interiors ---------- */
  const popup = qs('[data-lux-newsletter-popup]');
  if (popup instanceof HTMLDialogElement && typeof popup.showModal === 'function') {
    const KEY = 'luxNewsletterShown';
    const session = store('sessionStorage');
    let shown = false;
    try { shown = session?.getItem(KEY) === '1'; } catch (e) {}
    const open = () => {
      if (popup.open) return;
      // never cover something the visitor opened (e.g. a showcase photo): wait until it closes
      const busy = qsa('dialog').find((d) => d !== popup && d.open);
      if (busy) {
        busy.addEventListener('close', () => window.setTimeout(open, 1500), { once: true });
        return;
      }
      popup.showModal();
      try { session?.setItem(KEY, '1'); } catch (e) {}
    };
    qs('[data-lux-popup-close]', popup)?.addEventListener('click', () => popup.close());
    popup.addEventListener('click', (e) => { if (e.target === popup) popup.close(); });

    // returning from a popup sign-up: reopen it so the confirmation is seen
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && window.location.hash === `#${popup.dataset.formId}`) {
      open();
    } else if (!shown) {
      const trigger = document.querySelector(popup.dataset.trigger || '');
      if (trigger) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) { io.disconnect(); open(); }
        }, { threshold: 0.2 });
        io.observe(trigger);
      }
    }
  }
})();
